import { inject, Injectable } from '@angular/core';
import { ColumnState, FilterModel } from 'ag-grid-enterprise';
import { iif, Observable, of, throwError } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { AuthService, UserSession } from './auth.service';

export type UserPreferences = Partial<{
  id: number;
  username: string;
  tablesPreferences: {
    [key: string]:
      | ColumnState[]
      | {
          columnState: ColumnState[];
          filterState: FilterModel;
        };
  },
  userSettings: {
    emulateDBEnviroment?: boolean;
  } & Record<string, boolean>;
}>;

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private _preferences: Observable<UserPreferences>;

  private apiService = inject(ApiService);

  private authService = inject(AuthService);

  private dcAlertService = inject(DcAlertService);

  private _username: string;

  getPreferences(): Observable<UserPreferences> {
    return iif(
      () => !!this._preferences,
      this._preferences,
      this.authService.onUserSession.pipe(
        first(),
        switchMap((session: UserSession) => {
          const username = session.idToken.payload['cognito:username'];
          this._username = username;

          return this.apiService.get<UserPreferences>(
            'user-preferences',
            environment.api.ipb
          );
        }),
        map((preferences: UserPreferences) => {
          this._preferences = of(preferences);
          return preferences;
        }),
        catchError((error) => {
          this.dcAlertService.danger('Failed to load user preferences');
          return throwError(error);
        })
      )
    );
  }

  saveOrUpdate(preferences: Partial<UserPreferences>): Observable<void> {
    if (!this.getPreferences() && !preferences.id) {
      preferences.username = this._username;
      return this.apiService
        .post(`user-preferences`, preferences, environment.api.ipb)
        .pipe(
          tap((pref) => (this._preferences = of(pref))),
          first<void>(),
          catchError((error) => {
            this.dcAlertService.danger('Failed to save user preferences');
            return throwError(error);
          })
        );
    }

    return this.apiService
      .post(
        `user-preferences`,
        preferences,
        environment.api.ipb,
      )
      .pipe(
        tap((pref) => (this._preferences = of(pref))),
        first<void>(),
        catchError((error) => {
          this.dcAlertService.danger('Failed to update user preferences');
          return throwError(error);
        })
      );
  }

  // TODOG: Still unclear how this will be used for now
  deletePreferences(): Observable<void> {
    return this.apiService
      .delete(
        `user-preferences/${this._username}`,
        {},
        environment.api.productCapture
      )
      .pipe(
        first<void>(),
        tap(() => (this._preferences = null)),
        catchError((error) => {
          this.dcAlertService.danger('Failed to delete user preferences');
          return throwError(error);
        })
      );
  }

}
